import React, { useState, useEffect, useRef } from 'react';
import './TransaktionAnlegen.css';
import { createAbteilung, transformFormDataToAbteilung } from '../services/apiService';
import { 
  INITIAL_FORDERUNG,
  INITIAL_GRUNDPFANDRECHT,
  INITIAL_PFANDOBJEKT,
  INITIAL_UEBERTRAGUNGSBERECHTIGTER,
  isPfAktiv, 
  isGpAktiv, 
  validateFormData 
} from './TransaktionAnlegen.utils';
import Button from './ui/Button';
import Tabs from './ui/Tabs';
import GrunddatenForm from './GrunddatenForm';
import UebertragungsberechtigteForm from './UebertragungsberechtigteForm';
import ForderungenForm from './ForderungenForm';
import SicherheitenForm from './SicherheitenForm';

const TransaktionAnlegen = () => {
  // Hilfsfunktion zur Anzeige von Fehlern (unterstützt Arrays und einzelne Strings)
    const [errors, setErrors] = useState({});

    const renderFieldErrors = (fieldName) => {
      const fieldErrors = errors[fieldName];
      if (!fieldErrors) return null;

      if (Array.isArray(fieldErrors) && fieldErrors.length > 0) {
        return (
        <ul className="error-list">
          {fieldErrors.map((err, idx) => (
            <li key={idx} className="error-text">{err}</li>
          ))}
        </ul>
      );

    }
      if (typeof fieldErrors === 'string') {
        return <span className="error-text">{fieldErrors}</span>;

    }
      return null;

  };

  const [formData, setFormData] = useState({
    transaktion: '',
    kommentare: ''
  });

  // Persönliche Forderungen - Array für mehrere Blöcke
  const [persoenlicheForderungen, setPersoenlicheForderungen] = useState([INITIAL_FORDERUNG]);
    // Grundpfandrechte - Array für mehrere Blöcke

    const [grundpfandrechte, setGrundpfandrechte] = useState([INITIAL_GRUNDPFANDRECHT]);
    // Übertragungsberechtigte - Array für mehrere Blöcke

  const [uebertragungsberechtigte, setUebertragungsberechtigte] = useState([INITIAL_UEBERTRAGUNGSBERECHTIGTER]);
  const [activeTab, setActiveTab] = useState(0); // 0: Forderungen, 1: Sicherheiten
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error'
  const [submitMessage, setSubmitMessage] = useState('');

  // Ref für die Statusmeldung
  const statusMessageRef = useRef(null);

  // Automatisches Ausblenden der Meldung nach 10 Sekunden
  useEffect(() => {
    if (submitStatus) {
      const timer = setTimeout(() => {
        setSubmitStatus(null);
        setSubmitMessage('');
      }, 10000);

      return () => clearTimeout(timer);
    }
  }, [submitStatus]);

  // Automatisches Ausblenden der Fehlermeldungen nach 10 Sekunden
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      const timer = setTimeout(() => {
        setErrors({});
      }, 10000);

      return () => clearTimeout(timer);
    }
  }, [errors]);

  // Automatisches Scrollen zur Statusmeldung
  useEffect(() => {
    if (submitStatus && statusMessageRef.current) {
      statusMessageRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }, [submitStatus]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };


  const handlePersoenlicheForderungChange = (id, field, value) => {
    setPersoenlicheForderungen(prev => prev.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ));
    // Fehler für dieses Feld zurücksetzen
    const errorKey = `pf_${id}_${field}`;
    if (errors[errorKey]) {
      setErrors(prev => ({ ...prev, [errorKey]: null }));
    }
  };

  const addPersoenlicheForderung = () => {
    const newId = Math.max(...persoenlicheForderungen.map(f => f.id)) + 1;
    setPersoenlicheForderungen(prev => [...prev, { ...INITIAL_FORDERUNG, id: newId }]);
  };

  const removePersoenlicheForderung = (id) => {
    if (persoenlicheForderungen.length > 1) {
      setPersoenlicheForderungen(prev => prev.filter(item => item.id !== id));
    }
  };

  const handleGrundpfandrechtChange = (id, field, value) => {
    setGrundpfandrechte(prev => prev.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ));
    // Fehler für dieses Feld zurücksetzen
    const errorKey = `gp_${id}_${field}`;
    if (errors[errorKey]) {
      setErrors(prev => ({ ...prev, [errorKey]: null }));
    }
  };

  const addGrundpfandrecht = () => {
    const newId = Math.max(...grundpfandrechte.map(g => g.id)) + 1;
    setGrundpfandrechte(prev => [...prev, { ...INITIAL_GRUNDPFANDRECHT, id: newId, pfandobjekte: [{ ...INITIAL_PFANDOBJEKT, id: 1 }] }]);
  };

  const removeGrundpfandrecht = (id) => {
    if (grundpfandrechte.length > 1) {
      setGrundpfandrechte(prev => prev.filter(item => item.id !== id));
    }
  };

  // Berechne, ob Tabs den Pflichtfeld-Stern anzeigen sollen
  const isAnyPfAktiv = persoenlicheForderungen.some(f => isPfAktiv(f));
  const isAnyGpAktiv = grundpfandrechte.some(g => isGpAktiv(g));

  const handlePfandobjektChange = (grundpfandrechtId, pfandobjektId, field, value) => {

    // Validierung für PLZ: nur Zahlen erlauben
    if (field === 'identifikation.plz') {
      value = value.replace(/\D/g, ''); // Entferne alle Nicht-Zahlen
    }

    setGrundpfandrechte(prev => prev.map(gp => {
      if (gp.id === grundpfandrechtId) {
        return {
          ...gp,
          pfandobjekte: gp.pfandobjekte.map(po => {
            if (po.id === pfandobjektId) {
              // Unterstützung für verschachtelte Pfade wie "identifikation.ortGrundbuch"
              if (field.includes('.')) {
                const [parent, child] = field.split('.');
                return {
                  ...po,
                  [parent]: {
                    ...(po[parent] || {}),
                    [child]: value
                  }
                };
              }
              // Flache Felder
              return { ...po, [field]: value };
            }
            return po;
          })
        };
      }
      return gp;
    }));
    // Fehler für dieses Feld zurücksetzen, wenn vorhanden
    const errorKey = `po_${grundpfandrechtId}_${pfandobjektId}_${field}`;
    if (errors[errorKey]) {
      setErrors(prev => ({ ...prev, [errorKey]: null }));
    }
  };

  const addPfandobjekt = (grundpfandrechtId) => {
    setGrundpfandrechte(prev => prev.map(gp => {
      if (gp.id === grundpfandrechtId) {
        const newId = Math.max(...gp.pfandobjekte.map(po => po.id)) + 1;
        return {
          ...gp,
          pfandobjekte: [...gp.pfandobjekte, { ...INITIAL_PFANDOBJEKT, id: newId }]
        };
      }
      return gp;
    }));
  };

  const removePfandobjekt = (grundpfandrechtId, pfandobjektId) => {
    setGrundpfandrechte(prev => prev.map(gp => {
      if (gp.id === grundpfandrechtId && gp.pfandobjekte.length > 1) {
        return {
          ...gp,
          pfandobjekte: gp.pfandobjekte.filter(po => po.id !== pfandobjektId)
        };
      }
      return gp;
    }));
  };

  const handleUebertragungsberechtigtenChange = (id, field, value) => {
    // Validierung für PLZ: nur Zahlen erlauben
    if (field === 'postleitzahl') {
      value = value.replace(/\D/g, ''); // Entferne alle Nicht-Zahlen
    }

    setUebertragungsberechtigte(prev => prev.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const validateForm = (returnErrors = false) => {
    const newErrors = validateFormData(formData, persoenlicheForderungen, grundpfandrechte, uebertragungsberechtigte);

    if (returnErrors) return newErrors;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm(true);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setSubmitStatus('error');
      if (validationErrors.global) {
        setSubmitMessage(validationErrors.global);
      } else {
        setSubmitMessage('Bitte füllen Sie alle Pflichtfelder aus.');
      }
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);
    setSubmitMessage('');

    try {
      // Transformiere Formulardaten in Backend-Format
      const abteilungData = transformFormDataToAbteilung(
        formData,
        grundpfandrechte,
        uebertragungsberechtigte,
        persoenlicheForderungen
      );

      console.log('Sende Abteilung-Daten:', abteilungData);

      // Sende API-Request
      const response = await createAbteilung(abteilungData);

      setSubmitStatus('success');
      setSubmitMessage('Transaktion erfolgreich angelegt!');
      console.log('Transaktion erfolgreich:', response);

      // Optional: Formular zurücksetzen nach erfolgreicher Übermittlung
      setTimeout(() => {
        // Reset form oder Weiterleitung zur Übersichtsseite
      }, 2000);

    } catch (error) {
      setSubmitStatus('error');

      console.error('Fehler beim Anlegen der Transaktion:', error);
      console.log('Error Struktur:', {
        hasData: !!error.data,
        hasErrors: !!error.data?.errors,
        hasDetail: !!error.data?.detail,
        hasMessage: !!error.message,
        status: error.status,
        data: error.data,
        message: error.message
      });

      // Extrahiere Fehler vom Backend (apiService.js wirft { status, data, message })
      if (error.data?.errors && Object.keys(error.data.errors).length > 0) {
        // Mappe Backend-Fehler (z.B. "registereintraege[0].betragText") auf Frontend-Felder
        const fieldNameMapping = {
          'betragText': 'betrag',
          'umfangText': 'umfang',
          // Neues Mapping: Backend-Feld 'ortGrundbuch' soll unter "Grundbuch von" angezeigt werden
          'ortGrundbuch': 'grundbuchVon',
          'hausNr': 'hausnummer',
          // Übertragungsberechtigter-Felder
          'uebertragungsberechtigter.name': 'ubName',
          'uebertragungsberechtigter.strasse': 'ubStrasse',
          'uebertragungsberechtigter.hausNr': 'ubHausnummer',
          'uebertragungsberechtigter.plz': 'ubPlz',
          'uebertragungsberechtigter.ort': 'ubOrt',
          // Persönliche Forderung-Felder (Backend-Key direkt, da PflichtfeldValidierungException)
          'nameSchuldner': 'nameSchuldner',
          'vornameSchuldner': 'vornameSchuldner',
          'waehrung': 'waehrung',
          'betrag': 'betrag',
          'vorgangsNr': 'vorgangsNr',
        };

        const mappedErrors = {};
        Object.entries(error.data.errors).forEach(([fieldPath, messages]) => {
          let fieldName = fieldPath.includes('.')
            ? fieldPath.split('.').pop()
            : fieldPath;

          // Für PersoenlicheForderung-Fehler: mappe auf pf_1_<field> (erste Forderung als Fallback)
          const pfFields = ['nameSchuldner', 'vornameSchuldner', 'waehrung', 'betrag', 'vorgangsNr'];
          if (pfFields.includes(fieldName)) {
            fieldName = `pf_1_${fieldName}`;
          } else {
            fieldName = fieldNameMapping[fieldName] || fieldName;
          }

          if (mappedErrors[fieldName]) {
            if (Array.isArray(messages)) {
              mappedErrors[fieldName] = [...mappedErrors[fieldName], ...messages];
            } else {
              mappedErrors[fieldName].push(messages);
            }
          } else {
            mappedErrors[fieldName] = Array.isArray(messages) ? messages : [messages];
          }
        });

        console.log('Gemappte Fehler:', mappedErrors);
        setErrors(mappedErrors);

        // Wenn es auch ein detail gibt, zeige das an
        if (error.data.detail) {
          setSubmitMessage(error.data.detail);
        } else {
          setSubmitMessage('Bitte korrigieren Sie die markierten Fehler.');
        }
      } else if (error.data?.detail) {
        // Zeige die detail-Nachricht vom Backend
        setSubmitMessage(error.data.detail);
      } else if (error.message) {
        // Fallback auf error.message
        setSubmitMessage(error.message);
      } else {
        // Letzter Fallback
        setSubmitMessage(`Fehler beim Anlegen der Transaktion (Status: ${error.status || 'unbekannt'})`);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page-container">
      <h2 className="page-title">Transaktion anlegen</h2>

      <form onSubmit={handleSubmit} className="transaktion-form" id="transaktion-form">

        {/* Bereich 1: Kopfbereich - Grunddaten */}
        <GrunddatenForm 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          renderFieldErrors={renderFieldErrors} 
        />

        {/* Bereich 2: Übertragungsberechtigte */}
        <UebertragungsberechtigteForm 
          uebertragungsberechtigte={uebertragungsberechtigte} 
          handleUebertragungsberechtigtenChange={handleUebertragungsberechtigtenChange} 
          errors={errors} 
          renderFieldErrors={renderFieldErrors} 
        />

        {/* Tabs Bereich: Forderungen und Sicherheiten */}
        <div className="middle-block">
          <Tabs 
            tabs={[
              { label: <>Forderungen {isAnyPfAktiv && <span className="required-star">*</span>}</> }, 
              { label: <>Sicherheiten {isAnyGpAktiv && <span className="required-star">*</span>}</> }
            ]} 
            activeTab={activeTab} 
            onTabChange={setActiveTab} 
          />

          <div className="tab-content">
            {activeTab === 0 && (
              <ForderungenForm 
                persoenlicheForderungen={persoenlicheForderungen}
                addPersoenlicheForderung={addPersoenlicheForderung}
                removePersoenlicheForderung={removePersoenlicheForderung}
                handlePersoenlicheForderungChange={handlePersoenlicheForderungChange}
                errors={errors}
                renderFieldErrors={renderFieldErrors}
              />
            )}

            {activeTab === 1 && (
              <SicherheitenForm 
                grundpfandrechte={grundpfandrechte}
                addGrundpfandrecht={addGrundpfandrecht}
                removeGrundpfandrecht={removeGrundpfandrecht}
                handleGrundpfandrechtChange={handleGrundpfandrechtChange}
                addPfandobjekt={addPfandobjekt}
                removePfandobjekt={removePfandobjekt}
                handlePfandobjektChange={handlePfandobjektChange}
                errors={errors}
                renderFieldErrors={renderFieldErrors}
              />
            )}
          </div>
        </div>

        {/* Submit-Button */}
        <div className="form-actions">
          <Button type="submit" variant="primary" disabled={isSubmitting} ariaLabel="Transaktion anlegen" title="Transaktion anlegen">{isSubmitting ? 'Übermittle...' : 'Transaktion anlegen'}</Button>
        </div>

        {/* Statusmeldung */}
        {submitStatus && (
          <div className={`submit-status ${submitStatus}`} ref={statusMessageRef}>{submitMessage}</div>
        )}
      </form>
    </div>
  );
};

export default TransaktionAnlegen;


